import React from 'preact/compat';
import { FieldArray, useField, ArrayHelpers } from 'formik';
import Preview from './preview';
import DropTarget from './drop-target';
import { fileSize } from '../../../lib/file';
import CloseIcon from '../../icons/close';
import ImageIcon from '../../icons/image';

export default function AttachmentQueue({ name, id, help, mimeTypes = [], maxCount = 1 }: AttachmentQueueProps) {
    const [, meta, helpers] = useField<File[]>(name);
    const { value } = meta;
    const { setValue, setTouched } = helpers;
    const disabled = value.length === maxCount;

    function addFiles(files: FileList | File[]) {
        if (files instanceof FileList) {
            files = Array.from(files);
        }

        setValue(value.concat(files).slice(0, maxCount));
        setTouched(true);
    }

    function handleChange(event: Event) {
        const input = event.target as HTMLInputElement;
        const { files } = input;

        if (files === null) {
            return;
        }

        addFiles(files);
        input.value = '';
    }

    return (
        <FieldArray name={name} render={({ remove }: ArrayHelpers) => (
            <div className='attachment-queue'>
                <DropTarget allowedTypes={mimeTypes} onDrop={addFiles} disabled={disabled}>
                    <ImageIcon className='attachment-queue__icon' />
                    <div>
                        <input
                            type='file'
                            id={id}
                            accept={mimeTypes.join(', ')}
                            multiple={maxCount > 1}
                            onChange={handleChange}
                            disabled={disabled}
                            className='visually-hidden'
                        />
                        <label htmlFor={id} className='attachment-queue__label'>Browse files</label>
                        {' '}
                        or drag and drop {maxCount === 1 ? 'a file' : 'files'} here
                    </div>
                    {help && (
                        <div className='attachment-queue__help'>{help}</div>
                    )}
                </DropTarget>
                {value.length > 0 && (
                    <ol className='attachment-queue__items'>
                        {value.map((file, index) => (
                            <li key={file.name}>
                                <Attachment file={file} remove={() => remove(index)} />
                            </li>
                        ))}
                    </ol>
                )}
            </div>
        )} />
    );
}

type AttachmentQueueProps = {
    name: string,
    id: string,
    mimeTypes?: string[],
    maxCount?: number,
    help?: string,
};

function Attachment({ file, remove }: AttachmentProps) {
    const isImage = file.type.startsWith('image/');

    return (
        <div className='attachment-queue__file'>
            <div className='attachment-queue__preview-clip'>
                {isImage && <Preview file={file} className='attachment-queue__preview' />}
            </div>
            <div className='attachment-queue__file-info'>
                <span className='attachment-queue__file-name'>{file.name}</span>
                <span className='attachment-queue__file-size'>{fileSize(file.size)}</span>
            </div>
            <div>
                <button type='button' onClick={remove} aria-label='Remove this image' className='attachment-queue__button'>
                    <CloseIcon className='attachment-queue__x' />
                </button>
            </div>
        </div>
    );
}

type AttachmentProps = {
    file: File,
    remove: () => void,
};
